import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, User } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Blog = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isFrench = location.pathname.startsWith('/fr');
  
  const scrollToDemo = () => {
    const currentPath = window.location.pathname;
    const isFrenchPage = currentPath.startsWith('/fr');
    
    // If we're on the home page, scroll to the form
    if (currentPath === '/' || currentPath === '/fr') {
      const ctaSection = document.getElementById('cta');
      if (ctaSection) {
        ctaSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with language prefix
      const homeUrl = isFrenchPage ? '/fr?scrollTo=cta' : '/?scrollTo=cta';
      window.location.href = homeUrl;
    }
  };

  return (
    <Layout>
      <SEOHead 
        title="NoQ5.ai Blog: Salesforce & HubSpot AI Copilot Guides | CRM Automation Tips"
        description="Learn how NoQ5.ai transforms CRM automation. GDPR-compliant, voice-first AI copilot guides for sales teams using Salesforce and HubSpot."
        canonicalUrl="https://noq5.ai/blog"
        keywords="NoQ5.ai blog, Salesforce AI copilot, HubSpot AI copilot, CRM automation guides, sales AI tips, GDPR compliant AI, Gong alternative, Clari alternative"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "NoQ5.ai Blog",
            "url": "https://noq5.ai/blog",
            "publisher": {
              "@type": "Organization",
              "name": "NoQ5.ai",
              "logo": {
                "@type": "ImageObject",
                "url": "https://noq5.ai/images/noq5-logo.png"
              }
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Sales AI & CRM Copilot Resources",
            "url": "https://noq5.ai/blog"
          }
        ]}
      />
      <div className="container px-4 md:px-6 py-8 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Breadcrumbs items={[
            { label: 'Blog', href: '/blog' }
          ]} />

          <div className="mb-12 text-center">
            <div className="inline-block rounded-lg bg-oli-blue-light px-3 py-1 text-sm text-oli-blue mb-4">
              {t('blog.badge')}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              {t('blog.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('blog.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <Link 
              to="/blog/best-salesforce-ai-copilot-smbs-2025" 
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              aria-label="Read: Best CRM AI Copilot for SMBs (2025 Edition)"
            >
              <article>
                <h3 className="font-semibold text-lg mb-2">Best CRM AI Copilot for SMBs</h3>
                <p className="text-gray-600 text-sm mb-4">Compare Salesforce Agentforce, HubSpot Breeze, and other copilots - see why NoQ5.ai is the #1 choice for SMBs.</p>
                <div className="flex items-center text-gray-400 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  8 min read
                </div>
              </article>
            </Link>

            <Link 
              to="/blog/update-salesforce-automatically-2025" 
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              aria-label="Read: How to Update Salesforce Automatically Without Manual Entry (2025 Guide)"
            >
              <article>
                <h3 className="font-semibold text-lg mb-2">Update Salesforce Automatically (2025)</h3>
                <p className="text-gray-600 text-sm mb-4">Discover how to update Salesforce automatically without manual entry using the fastest, simplest AI Copilot.</p>
                <div className="flex items-center text-gray-400 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  7 min read
                </div>
              </article>
            </Link>
            
            <Link 
              to="/blog/why-sales-reps-hate-crms" 
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              aria-label="Read: Why Sales Reps Hate CRMs (and How AI Copilots Fix That)"
            >
              <article>
                <h3 className="font-semibold text-lg mb-2">Why Sales Reps Hate CRMs</h3>
                <p className="text-gray-600 text-sm mb-4">Sales reps hate CRMs because of manual data entry. Learn how AI copilots like NoQ5.ai fix adoption problems.</p>
                <div className="flex items-center text-gray-400 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  6 min read
                </div>
              </article>
            </Link>

            <Link 
              to="/blog/gdpr-compliant-salesforce-ai-copilot-europe" 
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              aria-label="Read: The Best GDPR-Compliant CRM AI Copilot (Built in Europe)"
            >
              <article>
                <h3 className="font-semibold text-lg mb-2">GDPR-Compliant CRM AI Copilot</h3>
                <p className="text-gray-600 text-sm mb-4">Need a CRM AI Copilot (Salesforce & HubSpot) that's GDPR-first and EU-based? Meet NoQ5.ai with built-in anonymisation.</p>
                <div className="flex items-center text-gray-400 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  9 min read
                </div>
              </article>
            </Link>
            
            <Link 
              to="/blog/how-to-update-salesforce-automatically" 
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              aria-label="Read: How to update Salesforce automatically without manual entry"
            >
              <article>
                <h3 className="font-semibold text-lg mb-2">How to Update Salesforce Automatically</h3>
                <p className="text-gray-600 text-sm mb-4">Learn how voice-first AI copilots eliminate manual data entry and save hours weekly.</p>
                <div className="flex items-center text-gray-400 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  5 min read
                </div>
              </article>
            </Link>
            
            <Link 
              to="/blog/ai-copilots-sales-teams-2025" 
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              aria-label="Read: The rise of AI copilots for sales teams in 2025"
            >
              <article>
                <h3 className="font-semibold text-lg mb-2">AI Copilots for Sales Teams 2025</h3>
                <p className="text-gray-600 text-sm mb-4">Explore the trends and competitive advantages of adopting AI copilots early.</p>
                <div className="flex items-center text-gray-400 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  6 min read
                </div>
              </article>
            </Link>

            <Link 
              to="/blog/salesforce-data-quality-ai-voice-assistants" 
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              aria-label="Read: Improving Salesforce Data Quality with AI Voice Assistants"
            >
              <article>
                <h3 className="font-semibold text-lg mb-2">Salesforce Data Quality & AI Voice</h3>
                <p className="text-gray-600 text-sm mb-4">Discover how AI voice assistants improve Salesforce data quality and boost sales team productivity.</p>
                <div className="flex items-center text-gray-400 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  7 min read
                </div>
              </article>
            </Link>

            <Link 
              to="/blog/hubspot-vs-salesforce-ai-integration-2025" 
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              aria-label="Read: HubSpot vs Salesforce AI Integration comparison"
            >
              <article>
                <h3 className="font-semibold text-lg mb-2">HubSpot vs Salesforce AI Integration</h3>
                <p className="text-gray-600 text-sm mb-4">Compare HubSpot and Salesforce AI capabilities and see how NoQ5.ai integrates with both platforms.</p>
                <div className="flex items-center text-gray-400 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  8 min read
                </div>
              </article>
            </Link>

            <Link 
              to="/blog/voice-assistant-technology-sales-teams-roi-calculator" 
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              aria-label="Read: Voice Assistant Technology ROI Calculator for Sales Teams"
            >
              <article>
                <h3 className="font-semibold text-lg mb-2">Voice Assistant ROI Calculator</h3>
                <p className="text-gray-600 text-sm mb-4">Calculate the ROI of voice assistant technology for your sales team with our comprehensive guide.</p>
                <div className="flex items-center text-gray-400 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  10 min read
                </div>
              </article>
            </Link>
          </div>

          <div className="bg-oli-blue text-white p-8 rounded-lg text-center">
            <h2 className="text-2xl font-bold mb-4">{t('blog.cta.title')}</h2>
            <p className="mb-6">
              {t('blog.cta.description')}
            </p>
            <Button 
              onClick={scrollToDemo}
              className="bg-white text-oli-blue hover:bg-gray-100"
            >
              <Link to={isFrench ? '/fr/app' : '/app'}>{t('blog.cta.button')}</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Blog;